import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Icon,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Link,
  Stack,
  Skeleton,
  SkeletonText,
  Tooltip,
  Spinner,
} from "@chakra-ui/react";
import {
  MdVisibility,
  MdVisibilityOff,
  MdZoomIn,
  MdZoomOut,
  MdGridOn,
  MdGridOff,
  MdColorLens,
  MdEqualizer,
  MdLink,
} from "react-icons/md";
import FeatureIcon from "./feature-icon";

import { observer } from "mobx-react-lite";

import { service } from "@uidaholib/shared/machines/map/map.machine";
import { useService } from "@xstate/react";
import { featureStore } from "@uidaholib/shared/models/features/features";
import { sortLayers } from "@uidaholib/shared/stores/layers/layers.helpers";
import { metadataStore } from "@uidaholib/shared/models/metadata/metadata";
import { findIdFor } from "@uidaholib/shared/stores/helpers";
import { helperInfo } from "@uidaholib/shared/models/ui/ui";
import { Analytic } from "@uidaholib/shared/api/analytics";
import { configStore } from "@uidaholib/shared/models/config/config";

const buttonIcons = [
  {
    type: "visible",
    onIcon: MdVisibility,
    offIcon: MdVisibilityOff,
  },
  {
    type: "zoomed",
    onIcon: MdZoomOut,
    offIcon: MdZoomIn,
  },
  {
    type: "footprintEnabled",
    onIcon: MdGridOn,
    offIcon: MdGridOff,
  },
  {
    type: "naturalColored",
    onIcon: MdColorLens,
    offIcon: MdColorLens,
  },
];

const CollectionItem = observer(({ feature }) => {
  const [state, send] = useService(service);

  const config = configStore.getConfig();

  const toggleModal = (e) => {
    let url = "";
    const layerId = feature.layerIds[0];
    if (state.matches("loaded")) {
      const layers = state.context.webmap.resourceInfo.operationalLayers;
      const layer = layers.find(
        (l) => findIdFor(l.layerDefinition) === layerId
      );
      url = layer.url;
    }
    metadataStore.toggleModalIsOpen();
    metadataStore.loadMetadata({ layerId, url });
    Analytic.sendEvent({
      label: feature.label,
      category: `${config.analytics.layers.category}-metadata`,
      action: feature.fieldName,
    });
  };
  return (
    <Box
      w="100%"
      justifyContent="space-between"
      display="flex"
      alignContent="center"
      alignItems="center"
      borderTop="1px"
      py={2}
      borderColor="gray.200"
      flexShrink={0}
      opacity={feature.disabled ? 0.5 : 1}
    >
      <Button
        p={0}
        ml={0}
        mr={2}
        colorScheme="indigo"
        variant="ghost"
        display="flex"
        flexWrap="wrap"
        onClick={toggleModal}
      >
        {feature.label}
      </Button>

      <Flex alignItems="center">
        {buttonIcons.map(({ offIcon, onIcon, type }) => (
          <FeatureIcon
            key={type}
            field={type}
            onIcon={onIcon}
            offIcon={offIcon}
            feature={feature}
            preventSelection={featureStore.hasReachedMaxSelection}
          />
        ))}
      </Flex>
    </Box>
  );
});

const Collection = observer(() => {

  const onClose = (e) => {
    metadataStore.closeModal();
  };

  return (
    <Box>
      <Modal onClose={onClose} isOpen={metadataStore.modalIsOpen}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{metadataStore.current && metadataStore.current.title}</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            {metadataStore.current ? (
              <Text noOfLines={6} isTruncated>
                {metadataStore.current.description}
              </Text>
            ) : (
              <Flex h={48} justifyContent="center" alignItems="center">
                <Spinner
                  thickness="2px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="lg"
                  label="loading..."
                />
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Flex>
              <Button colorScheme="gray" mr={2} onClick={onClose}>
                Close
              </Button>

              <Button colorScheme="green">
                <Link
                  href={metadataStore.current ? metadataStore.current.link : ""}
                  isExternal
                >
                  <Icon as={MdLink} />
                  More Information
                </Link>
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Tooltip
        zIndex={70}
        label="Click on a layer's name to view more information"
        isOpen={helperInfo.isOpen}
      >
        <Flex
          flexDir="column"
          w="100%"
          minH={0}
          maxH="100%"
          overflowY="auto"
          flexShrink={0}
        >
          {featureStore.datasets().map((feature) => (
            <CollectionItem key={feature.label} feature={feature} />
          ))}
        </Flex>
      </Tooltip>
    </Box>
  );
});

export default Collection;
